import React from 'react';
import {
  Button,
  Field,
  FieldContainer,
  FieldProps,
  Input,
} from '@odin-labs/components';
import { cn, useBoolean } from 'utils';
import { setRefFactory } from 'components/utils';
import { QrcodeIcon } from 'components/icons';
import { QrCodeScanner } from './QrCodeScanner';

export type QrCodeFieldProps = FieldProps & {
  cameraFacingMode?: 'environment' | 'user';
  parseQrCode?: (rawValue: string) => string;
  allowScanning?: boolean;
};

export type QrCodeFieldRef = HTMLInputElement & {
  open: () => void;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return 'Unsupported QR code format.';
};

export const QrCodeField = React.forwardRef<QrCodeFieldRef, QrCodeFieldProps>(
  (props, ref): React.ReactElement => {
    const {
      cameraFacingMode = 'environment',
      parseQrCode,
      allowScanning = true,
      ...fieldProps
    } = props;

    const {
      name,
      label,
      description,
      note,
      error: propsError,
      loading,
      ...restProps
    } = fieldProps;

    const {
      value: isScannerOpen,
      setTrue: openScanner,
      setFalse: closeScanner,
    } = useBoolean(false);

    const [scannerError, setScannerError] =
      React.useState<React.ReactNode>(null);

    const closeScannerAndClearError = React.useCallback((): void => {
      setScannerError(null);
      closeScanner();
    }, [closeScanner]);

    const handleDetected = React.useCallback(
      async (rawValue: string): Promise<boolean> => {
        try {
          const nextValue = parseQrCode ? parseQrCode(rawValue) : rawValue;
          props.onChange?.(nextValue);
          closeScannerAndClearError();
          return true;
        } catch (errorToHandle) {
          const nextError = new Error(getErrorMessage(errorToHandle));
          setScannerError(nextError.message);
          return false;
        }
      },
      [props.onChange, parseQrCode],
    );

    const setRef = setRefFactory<QrCodeFieldRef>({
      outerRef: ref,
    });

    if (!allowScanning) {
      return <Field {...fieldProps} ref={setRef} />;
    }

    const error = scannerError || propsError;

    return (
      <FieldContainer
        name={name}
        label={label}
        note={note}
        description={description}
        error={error}
        loading={loading}
      >
        <div
          className={cn('odin-flex odin-gap-2', isScannerOpen && 'odin-hidden')}
        >
          <div className="odin-flex-1">
            <Input
              name={name}
              hasError={!!error}
              {...restProps}
              ref={(input) =>
                setRef(input && Object.assign(input, { open: openScanner }))
              }
            />
          </div>
          <Button
            theme="secondary"
            icon={QrcodeIcon}
            onClick={openScanner}
            disabled={props.disabled}
            hideFocusBorder
          />
        </div>
        <QrCodeScanner
          isOpen={isScannerOpen}
          close={closeScannerAndClearError}
          cameraFacingMode={cameraFacingMode}
          onDetected={handleDetected}
        />
      </FieldContainer>
    );
  },
);

QrCodeField.displayName = 'QrCodeField';
